import React, { useState, useRef, useEffect } from 'react';
// NOTE: Radix Dialog NOT used here — its FocusScope focus trap intercepts the
// transient focus shift when the browser opens the native file picker, aborting it.
// Using a plain div overlay (same pattern as CheckInModal/CheckOutModal) instead.
import { Button } from '@/components/ui/button';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import {
  Upload,
  Clock,
  Camera,
  AlertCircle,
  MapPin,
  Loader2,
  X,
  ImagePlus,
  Calendar,
} from 'lucide-react';

// PhotoUploadArea — defined outside component to prevent unmount/remount on re-render.
// Uses invisible file input overlay (opacity:0, absolute, full-size) instead of onClick
// handlers or <label htmlFor> — both get intercepted by Radix Dialog's DismissableLayer.
// The native file input as the direct click target is the only reliable approach inside
// Radix Dialog portals across all browsers (desktop + mobile).
const PhotoUploadArea = ({ label, required, file, preview, onClear, inputRef, onChange, inputId, fileError }) => (
  <div>
    <p className="block text-sm font-medium text-gray-700 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </p>
    {preview ? (
      <div className="border-2 border-dashed rounded-xl p-4 text-center transition-colors border-green-300 bg-green-50/50 relative">
        <img
          src={preview}
          alt={label}
          className="max-h-28 mx-auto rounded-lg shadow-sm"
        />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onClear();
          }}
          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 shadow z-10"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <p className="text-xs text-green-600 mt-2 font-medium">Photo attached — tap to replace</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onChange(f);
            setTimeout(() => { e.target.value = ''; }, 100);
          }}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-[5]"
          tabIndex={-1}
        />
      </div>
    ) : (
      <div className="border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors border-gray-300 hover:border-[#7C1D3E]/50 hover:bg-[#7C1D3E]/5 relative">
        <div className="space-y-1.5 py-2 pointer-events-none">
          <Camera className="h-8 w-8 mx-auto text-gray-400" />
          <p className="text-sm text-gray-500">Tap to upload photo</p>
          <p className="text-xs text-gray-400">JPG, PNG, HEIC up to 10MB</p>
        </div>
        <input
          id={inputId}
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onChange(f);
            setTimeout(() => { e.target.value = ''; }, 100);
          }}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          tabIndex={-1}
        />
      </div>
    )}
    {fileError && !preview && (
      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
        <AlertCircle className="h-3 w-3" />
        {fileError}
      </p>
    )}
  </div>
);

/**
 * DailyRegularizationModal — submit a regularization request for a past date.
 *
 * Requires two mandatory proof photos (check-in + check-out), optional additional
 * evidence (up to 3), location selection, claimed times, and a reason.
 *
 * Props: { isOpen, onClose, selectedDate, onSuccess }
 */
export default function DailyRegularizationModal({ isOpen, onClose, selectedDate, onSuccess }) {
  // Form state
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [claimedCheckIn, setClaimedCheckIn] = useState('09:00');
  const [claimedCheckOut, setClaimedCheckOut] = useState('18:00');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Photo state
  const [checkInProof, setCheckInProof] = useState(null);
  const [checkInPreview, setCheckInPreview] = useState(null);
  const [checkOutProof, setCheckOutProof] = useState(null);
  const [checkOutPreview, setCheckOutPreview] = useState(null);
  const [additionalPhotos, setAdditionalPhotos] = useState([]); // { file, preview }[]

  // Refs
  const checkInFileRef = useRef(null);
  const checkOutFileRef = useRef(null);
  const additionalFileRef = useRef(null);

  // Fetch locations on open
  useEffect(() => {
    if (!isOpen) return;
    const fetchLocations = async () => {
      setLocationsLoading(true);
      try {
        const res = await axiosInstance.get('/attendance/daily/locations');
        setLocations(res.data?.locations || res.data || []);
      } catch {
        toast.error('Failed to load locations');
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedLocation('');
      setClaimedCheckIn('09:00');
      setClaimedCheckOut('18:00');
      setReason('');
      // Revoke object URLs to prevent memory leaks
      if (checkInPreview && checkInPreview.startsWith('blob:')) URL.revokeObjectURL(checkInPreview);
      if (checkOutPreview && checkOutPreview.startsWith('blob:')) URL.revokeObjectURL(checkOutPreview);
      additionalPhotos.forEach(p => {
        if (p.preview && p.preview.startsWith('blob:')) URL.revokeObjectURL(p.preview);
      });
      setCheckInProof(null);
      setCheckInPreview(null);
      setCheckOutProof(null);
      setCheckOutPreview(null);
      setAdditionalPhotos([]);
      setFileError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // File handlers
  const [fileError, setFileError] = useState(null);

  const handleFileSelect = (file, setFile, setPreview) => {
    if (!file) {
      console.error('[RegularizationModal] handleFileSelect called with no file');
      return;
    }
    setFileError(null);
    console.log('[RegularizationModal] File selected:', file.name, 'size:', file.size, 'type:', file.type);

    if (file.size > 10 * 1024 * 1024) {
      const msg = 'File too large. Max 10MB.';
      console.error('[RegularizationModal]', msg, file.size);
      toast.error(msg);
      setFileError(msg);
      return;
    }
    setFile(file);

    // Use createObjectURL for preview — works for HEIC and all image formats
    // unlike FileReader.readAsDataURL which produces unusable data URIs for HEIC
    try {
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
      console.log('[RegularizationModal] Preview set via createObjectURL');
    } catch (err) {
      console.error('[RegularizationModal] createObjectURL failed, falling back to FileReader:', err);
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.onerror = (e) => {
        console.error('[RegularizationModal] FileReader error:', e);
        const msg = 'Could not load photo preview. Please try a different image.';
        toast.error(msg);
        setFileError(msg);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.error('[RegularizationModal] handleAdditionalFile: no file selected');
      return;
    }
    if (additionalPhotos.length >= 3) {
      toast.error('Maximum 3 additional photos allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max 10MB.');
      return;
    }
    try {
      const objectUrl = URL.createObjectURL(file);
      setAdditionalPhotos((prev) => [...prev, { file, preview: objectUrl }]);
    } catch (err) {
      console.error('[RegularizationModal] additional photo createObjectURL failed:', err);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAdditionalPhotos((prev) => [...prev, { file, preview: reader.result }]);
      };
      reader.onerror = (e) => {
        console.error('[RegularizationModal] additional photo FileReader error:', e);
        toast.error('Could not load photo preview.');
      };
      reader.readAsDataURL(file);
    }
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const removeAdditionalPhoto = (idx) => {
    setAdditionalPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  // Validation
  const isValid =
    selectedDate &&
    selectedLocation &&
    claimedCheckIn &&
    claimedCheckOut &&
    claimedCheckIn < claimedCheckOut &&
    checkInProof &&
    checkOutProof &&
    reason.trim().length >= 10;

  // Submit
  const handleSubmit = async () => {
    if (!isValid) {
      if (!checkInProof || !checkOutProof) {
        toast.error('Both check-in and check-out proof photos are required');
      } else if (reason.trim().length < 10) {
        toast.error('Reason must be at least 10 characters');
      } else if (claimedCheckIn >= claimedCheckOut) {
        toast.error('Check-in time must be before check-out time');
      } else {
        toast.error('Please fill in all required fields');
      }
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('date', selectedDate);

      // Find the location object for extra metadata
      const loc = locations.find(
        (l) => (l.location_id || l._id || l.id) === selectedLocation
      );
      formData.append('location_id', selectedLocation);
      formData.append('location_name', loc?.location_name || loc?.name || selectedLocation || '');

      formData.append('claimed_check_in', claimedCheckIn);
      formData.append('claimed_check_out', claimedCheckOut);
      formData.append('reason', reason.trim());

      // Mandatory proof photos
      formData.append('check_in_proof', checkInProof, 'checkin_proof.jpg');
      formData.append('check_out_proof', checkOutProof, 'checkout_proof.jpg');

      // Optional additional evidence (backend expects additional_proof_1, _2, _3)
      additionalPhotos.forEach((p, i) => {
        formData.append(`additional_proof_${i + 1}`, p.file, `additional_${i + 1}.jpg`);
      });

      const res = await axiosInstance.post(
        '/attendance/daily/regularization/submit',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success(res.data?.message || 'Regularization request submitted successfully');
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      if (err.response?.status === 413) {
        toast.error('Photos are too large. Please use smaller or compressed images (max 10MB each).');
      } else {
        const detail = err.response?.data?.detail;
        toast.error(
          typeof detail === 'string' ? detail : detail?.message || 'Failed to submit request'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Format date for display
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'Asia/Kolkata',
      });
    } catch {
      return dateStr;
    }
  };

  // PhotoUploadArea is now defined outside the component (top of file) to prevent
  // unmount/remount on every re-render which was breaking file input clicks.

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={() => !submitting && onClose()}
      />
      {/* Modal panel */}
      <div className="relative z-50 bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto p-6">
        {/* Header */}
        <div className="mb-4">
          <h2 className="flex items-center gap-2 text-[#7C1D3E] text-lg font-semibold">
            <Calendar className="h-5 w-5" />
            Regularize Attendance
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Submit proof for a missed attendance day. Requires manager and admin approval.
          </p>
        </div>
        {/* Close button */}
        <button
          onClick={() => !submitting && onClose()}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="space-y-4 py-2">
          {/* Date (read-only) */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">Date</div>
            <div className="font-semibold text-gray-800">
              {formatDisplayDate(selectedDate)}
            </div>
          </div>

          {/* Location dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <MapPin className="h-3.5 w-3.5 inline mr-1" />
              Location <span className="text-red-500">*</span>
            </label>
            {locationsLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading locations...
              </div>
            ) : (
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#7C1D3E]/30 focus:border-[#7C1D3E] outline-none"
              >
                <option value="">Select location...</option>
                {locations.map((loc) => {
                  const id = loc.location_id || loc._id || loc.id;
                  return (
                    <option key={id} value={id}>
                      {loc.location_name || loc.name}
                      {loc.is_home_gym !== false ? ' (Home)' : ' (Visiting)'}
                    </option>
                  );
                })}
              </select>
            )}
          </div>

          {/* Claimed times */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Clock className="h-3.5 w-3.5 inline mr-1" />
                Check-in Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={claimedCheckIn}
                onChange={(e) => setClaimedCheckIn(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#7C1D3E]/30 focus:border-[#7C1D3E] outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Clock className="h-3.5 w-3.5 inline mr-1" />
                Check-out Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={claimedCheckOut}
                onChange={(e) => setClaimedCheckOut(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#7C1D3E]/30 focus:border-[#7C1D3E] outline-none"
              />
            </div>
          </div>

          {claimedCheckIn && claimedCheckOut && claimedCheckIn >= claimedCheckOut && (
            <p className="text-xs text-red-500 -mt-2">
              Check-in time must be before check-out time.
            </p>
          )}

          {/* Check-in Proof (Required) */}
          <PhotoUploadArea
            label="Check-in Proof (Required)"
            required
            inputId="regularize-checkin-photo"
            file={checkInProof}
            preview={checkInPreview}
            inputRef={checkInFileRef}
            onChange={(f) => handleFileSelect(f, setCheckInProof, setCheckInPreview)}
            onClear={() => {
              setCheckInProof(null);
              setCheckInPreview(null);
            }}
            fileError={fileError}
          />

          {/* Check-out Proof (Required) */}
          <PhotoUploadArea
            label="Check-out Proof (Required)"
            required
            inputId="regularize-checkout-photo"
            file={checkOutProof}
            preview={checkOutPreview}
            inputRef={checkOutFileRef}
            onChange={(f) => handleFileSelect(f, setCheckOutProof, setCheckOutPreview)}
            onClear={() => {
              setCheckOutProof(null);
              setCheckOutPreview(null);
            }}
            fileError={fileError}
          />

          {/* Additional Evidence (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <ImagePlus className="h-3.5 w-3.5 inline mr-1" />
              Additional Evidence (Optional, up to 3)
            </label>

            {/* Thumbnails of existing additional photos */}
            {additionalPhotos.length > 0 && (
              <div className="flex gap-2 mb-2 flex-wrap">
                {additionalPhotos.map((p, i) => (
                  <div key={i} className="relative">
                    <img
                      src={p.preview}
                      alt={`Evidence ${i + 1}`}
                      className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeAdditionalPhoto(i)}
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 shadow"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {additionalPhotos.length < 3 && (
              <div
                className="border-2 border-dashed border-gray-200 rounded-lg p-3 text-center cursor-pointer hover:border-gray-400 transition-colors"
                onClick={() => additionalFileRef.current?.click()}
              >
                <Upload className="h-5 w-5 mx-auto text-gray-400" />
                <p className="text-xs text-gray-500 mt-1">
                  Add photo ({additionalPhotos.length}/3)
                </p>
              </div>
            )}

            <input
              ref={additionalFileRef}
              type="file"
              accept="image/*"
              onChange={handleAdditionalFile}
              className="hidden"
            />
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Reason <span className="text-red-500">*</span>
              <span className="text-xs text-gray-400 ml-1">(min 10 characters)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why you missed checking in via the app..."
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#7C1D3E]/30 focus:border-[#7C1D3E] outline-none resize-none"
            />
            {reason.length > 0 && reason.trim().length < 10 && (
              <p className="text-xs text-red-500 mt-0.5">
                {10 - reason.trim().length} more characters needed
              </p>
            )}
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-blue-700">
              <p className="font-medium">Approval Process:</p>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>Your manager will review first, then admin gives final approval</li>
                <li>Both check-in and check-out proof photos are mandatory</li>
                <li>Only current and previous month dates allowed</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !isValid}
            className="bg-[#7C1D3E] hover:bg-[#6a1835] text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
